'use client'

/**
 * BudgetHero v3 — Bill prototype 2026-05-30 (app.go-atrium.com .bcard).
 *
 * White-surface card with green gradient .top4 strip across the top, "Monthly
 * ad spend limit" h3, an 8px gradient progress bar, .bigmono spend + "spent of $cap"
 * sub-line, and a dark "Change limit" button on the right of the dollar row.
 * "Resets on..." footer beneath.
 *
 * Live features preserved beyond the static prototype:
 *   - Progress bar SEGMENTS by channel type when breakdown data exists. The
 *     prototype shows a single gradient fill; here each segment is tinted
 *     with the v3 channel hex (lib/budget/channel-tokens-v3.ts) and stays
 *     clickable to scroll the matching channel group below.
 *   - Loading skeleton.
 *   - No-cap state surfaces a "Suggested cap: $X · Accept" hint.
 *   - Over-cap state recolors the bar red and surfaces an inline warning row.
 *   - EditSpendLimitPopover opens from the Change limit button.
 */

import { useCallback, useEffect, useState } from 'react'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import { getChannelTokenV3 } from '@/lib/budget/channel-tokens-v3'
import { EditSpendLimitPopover } from './EditSpendLimitPopover'
import { LimitCardFooter } from './LimitCardFooter'
import { fmtLimitCurrency } from './limit-card-utils'
import type { BudgetHeroResponse } from '@/app/api/budget/hero/route'

export interface BudgetHeroProps {
  /** Fired after wallet/cap likely changed (deposit, cap patch). */
  onReload?: () => void | Promise<void>
  /** Called by progress bar segment click to scroll+highlight a channel group. */
  onChannelGroupScroll?: (channelKey: string) => void
}

export type { BudgetHeroResponse }

function fmtCurrency(cents: number): string {
  return fmtLimitCurrency(cents)
}

export function BudgetHero({ onReload, onChannelGroupScroll }: BudgetHeroProps) {
  const [hero, setHero] = useState<BudgetHeroResponse | null>(null)
  const [loading, setLoading] = useState(true)

  const fetchHero = useCallback(async () => {
    try {
      const res = await fetch('/api/budget/hero')
      if (res.ok) setHero((await res.json()) as BudgetHeroResponse)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    void fetchHero()
  }, [fetchHero])

  const capCents = hero?.monthly_cap_cents ?? null
  const spentCents = hero?.mtd_spend_cents ?? 0
  const overCap = capCents !== null && spentCents > capCents
  const spentPct =
    capCents != null && capCents > 0 ? Math.min((spentCents / capCents) * 100, 100) : 0
  const breakdown = hero?.channel_type_breakdown ?? []

  return (
    <div className="bv3-bcard">
      <h3>Monthly ad spend limit</h3>
      <div className="h3sub">Maximum ad spend for this billing period.</div>

      {loading ? (
        <div className="bv3-prog" aria-hidden="true">
          <div style={{ width: '0%' }} />
        </div>
      ) : (
        <ProgressBar
          spentPct={spentPct}
          overCap={overCap}
          breakdown={breakdown}
          capCents={capCents}
          spentCents={spentCents}
          onChannelGroupScroll={onChannelGroupScroll}
        />
      )}

      {loading ? (
        <>
          <div className="h-9 w-56 rounded bg-[#F1EFE8] animate-pulse dark:bg-warm" />
          <div className="mt-4 h-4 w-40 rounded bg-[#F1EFE8]/60 animate-pulse dark:bg-warm" />
        </>
      ) : (
        <LimitCardFooter
          amount={fmtCurrency(spentCents)}
          sublabel={
            capCents !== null
              ? `spent of ${fmtCurrency(capCents)} this month${overCap ? '' : ''}`
              : 'spent this month'
          }
          changeLimitControl={
            <EditSpendLimitPopover
              currentCapCents={capCents}
              onSaved={() => {
                void fetchHero()
                onReload?.()
              }}
              trigger={
                <Button type="button" variant="outline" size="sm">
                  Change limit
                </Button>
              }
            />
          }
        />
      )}

      {!loading && overCap && (
        <div className="mt-3 rounded-lg border border-red-200 bg-red-50 px-4 py-2.5 text-xs text-red-700 dark:border-red-900/60 dark:bg-red-950/40 dark:text-red-300">
          MTD spend ({fmtCurrency(spentCents)}) exceeded cap (
          {fmtCurrency(capCents!)}). Smart allocation paused until next month.
        </div>
      )}

      {!loading &&
        capCents === null &&
        hero != null &&
        hero.recommended_default_cap_cents > 0 && (
          <SuggestedCap
            recommendedCents={hero.recommended_default_cap_cents}
            onAccept={async () => {
              await fetch('/api/budget/cap', {
                method: 'PATCH',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                  monthly_cap_cents: hero.recommended_default_cap_cents,
                }),
              })
              void fetchHero()
              onReload?.()
            }}
          />
        )}
    </div>
  )
}

function ProgressBar({
  spentPct,
  overCap,
  breakdown,
  capCents,
  spentCents,
  onChannelGroupScroll,
}: {
  spentPct: number
  overCap: boolean
  breakdown: BudgetHeroResponse['channel_type_breakdown']
  capCents: number | null
  spentCents: number
  onChannelGroupScroll?: (channelKey: string) => void
}) {
  const breakdownTotal = breakdown.reduce((s, b) => s + b.spend_cents, 0)
  const hasSegments = breakdown.length > 0 && breakdownTotal > 0

  const ariaLabel =
    capCents != null
      ? `Spent ${fmtCurrency(spentCents)} of ${fmtCurrency(capCents)} cap`
      : `Spent ${fmtCurrency(spentCents)} this month`

  return (
    <div
      role="progressbar"
      aria-valuenow={spentCents}
      aria-valuemax={capCents ?? undefined}
      aria-label={ariaLabel}
      className={cn('bv3-prog', overCap && 'over')}
    >
      {hasSegments ? (
        <div
          style={{
            display: 'flex',
            height: '100%',
            width: `${Math.max(Math.min(spentPct, 100), 4)}%`,
            transition: 'width 500ms ease',
          }}
        >
          {breakdown.map((seg) => {
            const segPct = (seg.spend_cents / breakdownTotal) * 100
            const token = getChannelTokenV3(seg.channel_key)
            return (
              <Button
                key={seg.channel_key}
                type="button"
                variant="ghost"
                aria-label={`${token.label} — click to scroll to channel group`}
                onClick={() => {
                  onChannelGroupScroll?.(seg.channel_key)
                  const el = document.getElementById(`channel-group-${seg.channel_key}`)
                  if (!el) return
                  el.scrollIntoView({ behavior: 'smooth', block: 'start' })
                  el.classList.add('ring-2', 'ring-offset-1', 'ring-atrium-black/30', 'rounded-xl')
                  setTimeout(() => {
                    el.classList.remove(
                      'ring-2',
                      'ring-offset-1',
                      'ring-atrium-black/30',
                      'rounded-xl',
                    )
                  }, 1200)
                }}
                className="h-full min-w-0 shrink cursor-pointer rounded-none p-0 hover:bg-transparent focus:outline-none"
                style={{
                  width: `${segPct}%`,
                  background: overCap ? '#EF4444' : token.hex,
                  border: 0,
                  padding: 0,
                  minWidth: segPct > 0 ? 2 : 0,
                }}
              />
            )
          })}
        </div>
      ) : (
        <div style={{ width: `${Math.max(spentPct, 0.5)}%` }} />
      )}
    </div>
  )
}

function SuggestedCap({
  recommendedCents,
  onAccept,
}: {
  recommendedCents: number
  onAccept: () => Promise<void>
}) {
  return (
    <div className="mt-3 flex items-center gap-2 text-sm">
      <span style={{ color: '#5A5A5A' }}>
        Suggested cap:{' '}
        <span style={{ color: '#1A1A1A', fontWeight: 600 }}>{fmtCurrency(recommendedCents)}</span>
      </span>
      <Button
        type="button"
        variant="link"
        onClick={() => void onAccept()}
        className="h-auto p-0 text-[14px] font-semibold"
        style={{ color: 'var(--color-brand)' }}
      >
        Accept
      </Button>
    </div>
  )
}
